import { InputAdornment, InputLabel, MenuItem, Select, TextField } from "@material-ui/core";
import CameraAltIcon from "@material-ui/icons/CameraAlt";
import { Api, App, User } from "app-interfaces";
import React from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router";

import { metricConverter, metricUnit, useStateValue } from "../../config/AppContext";
import { Action, ScoreType } from "../../types/StaticTypes";
import LoadingSpinner from "../Utilities/LoadingSpinner";
import { BirthDatePicker } from "../Utilities/DatePicker";
import Icons from "../Utilities/Icons";

type Props = {
  id: string;
  onSubmit?: () => void;
};

export type IdRoute = RouteComponentProps<{ id: string }>;
export type NoPathParamsRoute = RouteComponentProps<{}>;

export function ClientEditRoute(props: IdRoute) {
  return <ClientEdit id={props.match.params.id} onSubmit={() => props.history.goBack()} />;
}

export default function ClientEdit(props: Props) {
  const { t } = useTranslation();

  const { ic_user } = Icons;
  const { Methods, clients, countries, metric } = useStateValue() as App.State;

  const clientToBe = props.id;

  const initialClient =
    clients &&
    clients.find((client) => {
      return client.id === clientToBe;
    });

  const clientReducer = (client: User.IUserClient, { type, payload }: Action) => {
    switch (type) {
      case "update":
        return { ...client, ...payload };
      default:
        throw new Error();
    }
  };
  const [previewProfile, setPreviewProfile] = React.useState(`url(${ic_user})`);
  const [currentClient, setCurrentClient] = React.useReducer(clientReducer, initialClient);

  if (!clients || !Methods || !countries) return <LoadingSpinner />;

  if (!currentClient) return <> {t("No client found")} </>;

  if (currentClient.photo && previewProfile === `url(${ic_user})`) {
    setPreviewProfile(`url(${currentClient.photo.small})`);
  }

  const generateCountryList = () => {
    return countries.map((country: User.IUserCountry, i: number) => (
      <option key={i} value={country.code}>
        {country.name}
      </option>
    ));
  };

  const handleSubmit = async (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formInputs = document.querySelectorAll("input.form-control[required]");
    if ([...formInputs].find((formInput) => formInput.hasAttribute("invalid"))) return;
    let params: Api.Client["params"] = {};
    const formData = new FormData(e.currentTarget);
    formData.forEach((v, k) => {
      if (typeof v !== "object" && k === "weight")
        return Object.assign(params, {
          [k]: metricConverter(parseFloat(v), `${ScoreType[4]}O`, metric),
        });
      if (typeof v !== "object" && k === "height")
        return Object.assign(params, {
          [k]: metricConverter(parseFloat(v), "heightO", metric),
        });
      if (typeof v === "string") return Object.assign(params, { [k]: v.trim() });
      return Object.assign(params, { [k]: v });
    });
    Object.assign(params, { id: props.id });
    await Methods.putClient(params, props.id);

    // optional hook
    props.onSubmit && props.onSubmit();
  };

  const handleErrors = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.currentTarget.toggleAttribute("invalid", true);
    const formErrors = e.currentTarget.nextSibling as HTMLSpanElement | null;
    if (!formErrors) return;
    const inputName = e.currentTarget.name;
    const inputValue = e.currentTarget.value;
    if (!inputValue || inputValue === "" || inputValue.trim() === "") {
      setCurrentClient({
        type: "update",
        payload: { [inputName]: inputValue },
      });
      return (formErrors.innerText = `${e.currentTarget.name.replace("_", " ")} is required.`);
    }
    e.currentTarget.toggleAttribute("invalid", false);
    formErrors.innerText = "";
    return setCurrentClient({
      type: "update",
      payload: { [inputName]: inputValue },
    });
  };
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newImage = e.currentTarget.files && e.currentTarget.files[0];
    const image = await Methods.updateImage(newImage);
    const preview = document.getElementById("profile-preview");
    const photo_id = document.getElementById("photo_id") as HTMLInputElement | null;
    if (!newImage) return;
    setPreviewProfile(`url(${image.small})`);
    if (preview && photo_id) {
      // preview.innerText = 'Update profile image';
      photo_id.value = image.id;
      preview.append(photo_id);
      return;
    }
  };
  const triggerImageUpload = () => {
    const file = document.getElementById("file") as HTMLInputElement | null;
    file && file.click();
  };

  return (
    <div id="client-add">
      <input id="file" type="file" style={{ display: "none" }} onChange={handleImageUpload} />
      <form onSubmit={handleSubmit} onReset={(e) => e.preventDefault()}>
        <section
          id="profile-preview"
          onClick={triggerImageUpload}
          style={{
            display: "block",
            width: "120px",
            height: "120px",
            margin: "0 auto 35px auto",
            borderRadius: "50%",
            textAlign: "center",
            backgroundImage: previewProfile,
            backgroundColor: "#ffffff",
            backgroundRepeat: "no-repeat",
            backgroundSize: "100%",
            position: "relative",
          }}
        >
          <input
            id="photo_id"
            name="photo_id"
            type="text"
            style={{ visibility: "hidden" }}
            defaultValue={currentClient.photo && currentClient.photo.id}
          />
          <span
            style={{
              position: "absolute",
              bottom: -15,
              right: 36,
              padding: 10,
              background: "#007bff",
              borderRadius: "100%",
              width: 43,
            }}
          >
            {/* <FaCamera color="white" /> */}
            <CameraAltIcon style={{ color: "white" }} />
          </span>
        </section>
        <section className="d-flex justify-content-between">
          <TextField
            InputLabelProps={{
              style: { margin: "auto", fontWeight: "bold", fontSize: 17 },
              shrink: true,
            }}
            label={t("First name")}
            autoFocus={true}
            id="first_name"
            name="first_name"
            type="text"
            defaultValue={currentClient.first_name}
            // onChange={(e) => setState({ ...state, first_name: e.target.value })}
            onChange={handleErrors}
            // onBlur={handleErrors}

            // maxLength={18}
            // autoFocus={true}
            required
            title=""
            onInvalid={() => {
              const x = document.getElementById("first_name") as HTMLInputElement;
              x?.setCustomValidity(`${t("First Name cannot be empty.")}`);
            }}
            onInput={() => {
              const x = document.getElementById("first_name") as HTMLInputElement;
              x?.setCustomValidity(``);
            }}
          />
          <TextField
            InputLabelProps={{
              style: { margin: "auto", fontWeight: "bold", fontSize: 17 },
              shrink: true,
            }}
            label={t("Last name")}
            id="last_name"
            name="last_name"
            type="text"
            defaultValue={currentClient.last_name}
            // onChange={(e: any) => setState({ ...state, last_name: e.target.value })}
            onChange={handleErrors}
            // onBlur={handleErrors}
            // maxLength={18}
            required
            title=""
            onInvalid={() => {
              const x = document.getElementById("last_name") as HTMLInputElement;
              x?.setCustomValidity(`${t("Last Name cannot be empty.")}`);
            }}
            onInput={() => {
              const x = document.getElementById("last_name") as HTMLInputElement;
              x?.setCustomValidity(``);
            }}
          />
          {/* <div className='form-group'>
            <label htmlFor='first_name'>First Name</label>
            <input
              id='first_name'
              name='first_name'
              type='text'
              className='form-control'
              maxLength={18}
              required
              onChange={handleErrors}
              onBlur={handleErrors}
              autoFocus={true}
            />
            <span className='form-errors'></span>
          </div>
          <div className='form-group'>
            <label htmlFor='last_name'>Last Name</label>
            <input
              id='last_name'
              name='last_name'
              type='text'
              className='form-control'
              maxLength={18}
              required
              onChange={handleErrors}
              onBlur={handleErrors}
            />
            <span className='form-errors'></span>
          </div> */}
        </section>
        <h5
          className="section-heading"
          style={{
            background: "rgb(229 229 229 / 40%)",
            color: "#007bff",
            marginTop: 20,
            marginBottom: 10,
            textTransform: "none",
            fontSize: 14,
            padding: "10px 21px",
            width: 500,
            marginLeft: -21,
          }}
        >
          {t("About the client")}
        </h5>
        <section>
          <div className="form-group d-flex justify-content-between">
            {/* <label htmlFor='gender'>Gender</label> */}
            {/* <select id='gender' name='gender' className='form-control'>
              <option value='MALE'>Male</option>
              <option value='FEMALE'>Female</option>
            </select> */}
            <div style={{ width: "45%" }}>
              <InputLabel htmlFor="gender" style={{ fontSize: 14, fontWeight: "bold" }}>
                {t("Gender")}
              </InputLabel>
              <Select
                id="gender"
                name="gender"
                // onChange={(e: any) => setState({ ...state, gender: e.target.value })}
                defaultValue={currentClient.gender}
                style={{ width: "100%" }}
              >
                <MenuItem value="MALE">{t("Male")}</MenuItem>
                <MenuItem value="FEMALE">{t("Female")}</MenuItem>
              </Select>
            </div>
            <div style={{ width: "45%" }}>
              <InputLabel htmlFor="Birthday" style={{ fontSize: 14, fontWeight: "bold" }}>
                {t("Birthday")}
              </InputLabel>
              <BirthDatePicker
                defaultValue={currentClient.birthday}
                // onChange={(date: any) => setState({ ...state, birthday: date.toLocaleDateString().replace(/\//g, '-') })}
              />
            </div>
          </div>
          {/* <div>
            <label htmlFor='birthday'>Birth Date</label>
            <BirthDatePicker />
          </div> */}
          <div className="form-group d-flex justify-content-between">
            <div className="d-flex justify-content-between" style={{ width: "45%" }}>
              <TextField
                label={t("Height")}
                InputLabelProps={{
                  style: { margin: "auto", fontWeight: "bold", fontSize: 17 },
                  shrink: true,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <span style={{ color: "black" }}>{metricUnit("height", metric)}</span>
                    </InputAdornment>
                  ),
                }}
                type="number"
                id="height"
                name="height"
                style={{ width: "48%" }}
                defaultValue={metricConverter(currentClient.height, "height", metric)}
                // onChange={(e: any) => setState({ ...state, height: e.target.value })}
              />
              <TextField
                label={t("Weight")}
                InputLabelProps={{
                  style: { margin: "auto", fontWeight: "bold", fontSize: 17 },
                  shrink: true,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <span style={{ color: "black" }}>
                        {metricUnit(`${ScoreType[4]}`, metric)}
                      </span>
                    </InputAdornment>
                  ),
                }}
                type="number"
                id="weight"
                name="weight"
                style={{ width: "48%" }}
                defaultValue={metricConverter(currentClient.weight, `${ScoreType[4]}`, metric)}
              />
            </div>
            <div style={{ width: "45%" }}>
              <TextField
                InputLabelProps={{
                  style: { margin: "auto", fontWeight: "bold", fontSize: 17 },
                  shrink: true,
                }}
                label={t("Occupation")}
                type="text"
                id="occupation"
                name="occupation"
                defaultValue={currentClient.occupation}
                // onChange={(e: any) => setState({ ...state, occupation: e.target.value })}
              />
            </div>
            {/* <label htmlFor='height'>
              Height {metricUnit('height', metric)}
            </label>
            <input
              type='number'
              id='height'
              name='height'
              min={0}
              step={0.01}
              className='form-control'
            /> */}
            {/* <label htmlFor='weight'>
              Weight {metricUnit(`${ScoreType[4]}`, metric)}
            </label>
            <input
              type='number'
              id='weight'
              name='weight'
              min={0}
              step={0.01}
              className='form-control'
            /> */}
          </div>
          {/* <div className='form-group'>
            <label className='form-label' htmlFor='occupation'>
              Occupation
            </label>
            <input
              type='text'
              id='occupation'
              name='occupation'
              className='form-control'
            />
          </div> */}
        </section>
        <h5
          className="section-heading"
          style={{
            background: "rgb(229 229 229 / 40%)",
            color: "#007bff",
            marginTop: 20,
            marginBottom: 20,
            textTransform: "none",
            fontSize: 14,
            padding: "10px 21px",
            width: 500,
            marginLeft: -21,
          }}
        >
          {t("Contact information")}
        </h5>
        <section>
          <div className="form-group d-flex justify-content-between w-100">
            <TextField
              InputLabelProps={{
                style: { margin: "auto", fontWeight: "bold", fontSize: 17 },
                shrink: true,
              }}
              style={{ width: "45%" }}
              label={t("Phone")}
              type="text"
              id="phone"
              name="phone"
              defaultValue={currentClient.phone}
            />
            <TextField
              InputLabelProps={{
                style: { margin: "auto", fontWeight: "bold", fontSize: 17 },
                shrink: true,
              }}
              style={{ width: "45%" }}
              label={t("Email")}
              type="text"
              id="email"
              name="email"
              defaultValue={currentClient.email}
              // onChange={(e: any) => setState({ ...state, email: e.target.value })}
              onChange={handleErrors}
              // onBlur={handleErrors}
              // onBlur={handleErrors}
              required
              title=""
              onInvalid={() => {
                const x = document.getElementById("email") as HTMLInputElement;
                x?.setCustomValidity(`${t("Email cannot be empty.")}`);
              }}
              onInput={() => {
                const x = document.getElementById("email") as HTMLInputElement;
                x?.setCustomValidity(``);
              }}
            />
          </div>
          <div className="form-group d-flex justify-content-between">
            <div style={{ width: "45%" }}>
              <InputLabel
                htmlFor="country_code"
                style={{ fontSize: 14, fontWeight: "bold", marginTop: 0 }}
              >
                {t("Country")}
              </InputLabel>
              <Select
                id="country_code"
                name="country_code"
                defaultValue={currentClient.country && currentClient.country.code}
                // onChange={(e: any) => setState({ ...state, country_code: e.target.value })}
                style={{ width: "100%" }}
              >
                <MenuItem value=" ">{t("Select")}</MenuItem>
                {generateCountryList()}
              </Select>
            </div>
            <div style={{ width: "45%" }}>
              <TextField
                InputLabelProps={{
                  style: { margin: "auto", fontWeight: "bold", fontSize: 17 },
                  shrink: true,
                }}
                style={{ width: "100%" }}
                label={t("City")}
                type="text"
                id="city"
                name="city"
                defaultValue={currentClient.city && currentClient.city}
              />
              {/*<InputLabel
                htmlFor='city'
                style={{ fontSize: 14, fontWeight: 'bold', marginTop: 0 }}
              >
                {t('CITY')}
              </InputLabel>
               <Select
                id='city'
                name='city'
                defaultValue={currentClient.city}
                // onChange={(e: any) => setState({ ...state, city: e.target.value })}
                style={{ width: '100%' }}
              >
                <MenuItem value='null'>{t('Select')}</MenuItem>
                <MenuItem value='City 1'>City 1</MenuItem>
                <MenuItem value='City 2'>City 2</MenuItem>
                <MenuItem value='City 3'>City 3</MenuItem>
              </Select> */}
            </div>
          </div>
          <div className="form-group d-flex justify-content-between w-100">
            <TextField
              InputLabelProps={{
                style: { margin: "auto", fontWeight: "bold", fontSize: 17 },
                shrink: true,
              }}
              style={{ width: "45%" }}
              label={t("Street")}
              type="text"
              id="street"
              name="street"
              defaultValue={currentClient.street}
              // onChange={(e: any) => setState({ ...state, street: e.target.value })}
            />
            <TextField
              InputLabelProps={{
                style: { margin: "auto", fontWeight: "bold", fontSize: 17 },
                shrink: true,
              }}
              style={{ width: "45%" }}
              label={t("Postal code")}
              type="number"
              id="postal_code"
              name="postal_code"
              defaultValue={currentClient.postal_code}
              // onChange={(e: any) => setState({ ...state, postal_code: e.target.value })}
            />
          </div>
          <TextField
            InputLabelProps={{
              style: { margin: "auto", fontWeight: "bold", fontSize: 17 },
              shrink: true,
            }}
            label={t("Notes")}
            id="notes"
            name="notes"
            type="text"
            fullWidth
            defaultValue={currentClient.notes}
            // onChange={(e: any) => setState({ ...state, notes: e.target.value })}
          />
          {/* <div className='form-group'>
            <label htmlFor='email'>Email</label>
            <input
              type='text'
              id='email'
              name='email'
              className='form-control'
              required
              onChange={handleErrors}
              onBlur={handleErrors}
            />
            <span className='form-errors'></span>
          </div> */}
          {/* <div className='form-group'>
            <label htmlFor='country_code'>Country</label>
            <select
              id='country_code'
              name='country_code'
              className='form-control'
              defaultValue=''
            >
              <option value=''></option>
              {generateCountryList()}
            </select>
            <div className='form-group'>
              <label htmlFor='phone'>Phone number</label>
              <input
                type='text'
                id='phone'
                name='phone'
                className='form-control'
              />
            </div>
            <label htmlFor='city'>City</label>
            <input type='text' id='city' name='city' className='form-control' />
          </div> */}
          {/* <div className='form-group'>
            <label htmlFor='street'>Street</label>
            <input
              type='text'
              id='street'
              name='street'
              className='form-control'
            />
            <label htmlFor='postal_code'>Postal Code</label>
            <input
              type='number'
              id='postal_code'
              name='postal_code'
              className='form-control'
            />
          </div>
          <div className='form-group'>
            <label htmlFor='notes'>Notes</label>
            <textarea
              rows={3}
              id='notes'
              name='notes'
              className='form-control'
            />
          </div> */}
          <div className="button-group mt-4">
            <button
              type="submit"
              className="btn btn-primary w-100"
              // style={{ float: 'right' }}
            >
              {t("Update")}
            </button>
          </div>
        </section>
      </form>
      <style jsx={true}>
        {`
          .MuiFormLabel-root {
            color: #9b9b9bad;
          }
        `}
      </style>
    </div>
  );
}
